import React, { useState } from 'react';
import { useAuth } from './SupabaseAuth';
import { Shield, ArrowLeft, Eye, EyeOff } from 'lucide-react';

interface SupabaseLoginProps {
  onBack: () => void;
}

export const SupabaseLogin: React.FC<SupabaseLoginProps> = ({ onBack }) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const { error: signInError } = await signIn(email, password);
      
      if (signInError) {
        console.error('🔴 Login error:', signInError);
        
        // Handle specific refresh token errors
        if (signInError.message?.includes('Invalid Refresh Token') || 
            signInError.message?.includes('Refresh Token Not Found')) {
          setError('Previous session expired. Please try logging in again.');
          setShowAdvanced(true); // Show advanced options for persistent issues
        } else if (signInError.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials.');
        } else if (signInError.message?.includes('Email not confirmed')) {
          setError('Please confirm your email address before logging in.');
        } else if (signInError.message?.includes('Too many requests')) {
          setError('Too many login attempts. Please wait a moment and try again.');
        } else {
          setError(signInError.message || 'Invalid credentials. Please try again.');
        }
      }
    } catch (error) {
      console.error('🔴 Login exception:', error);
      
      // Handle refresh token errors in catch block
      if (error instanceof Error && (error.message?.includes('Invalid Refresh Token') || 
          error.message?.includes('Refresh Token Not Found'))) {
        setError('Session data was corrupted. The page will refresh to clear it.');
        setShowAdvanced(true);
        // Auto-refresh the page to clear any corrupted session data
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear all session data manually
  const handleClearSessionData = () => {
    try {
      console.log('🧹 Manual session clear requested');
      
      // Clear all localStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Also clear any service worker caches if they exist
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }
      
      setError('Session data cleared. Refreshing page...');
      
      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('🔴 Error clearing session data:', error);
      setError('Could not clear session data. Please try refreshing the page manually.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-purple-500/5 to-orange-500/5"></div>
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 211, 238, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 211, 238, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      {/* Floating Glitch Lines */}
      <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent cyberpunk-sheen"></div>
      <div className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent" style={{ animationDelay: '1.5s' }}></div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-800/50 border border-cyan-500/20 mb-6 relative group">
              <Shield className="h-8 w-8 text-cyan-400 cyberpunk-glow" />
              <div className="absolute inset-0 rounded-full bg-cyan-400/5 scale-0 group-hover:scale-100 transition-transform duration-500"></div>
            </div>
            
            <h1 className="text-2xl font-cyberpunk text-white mb-2 tracking-wider">
              INVESTOR
              <span className="block text-cyan-400 cyberpunk-glow">PORTAL</span>
            </h1>
            
            <div className="text-gray-400 font-cyberpunk-nav text-xs uppercase tracking-widest">
              Cosmic Forces • Secure Access
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-slate-800/30 backdrop-blur-sm border border-cyan-500/10 rounded-xl p-8 shadow-2xl relative overflow-hidden">
            
            {/* Card Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 rounded-xl"></div>
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent"></div>
            
            <div className="relative z-10">
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-xs font-cyberpunk-nav text-cyan-400 uppercase tracking-widest">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all duration-300 font-cyberpunk-nav text-sm"
                    placeholder="Enter your email"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-xs font-cyberpunk-nav text-cyan-400 uppercase tracking-widest">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 pr-12 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all duration-300 font-cyberpunk-nav text-sm"
                      placeholder="Enter your password"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition-colors duration-200"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-3">
                    <div className="text-red-400 text-sm font-cyberpunk-nav">
                      {error}
                    </div>
                    
                    {/* Advanced Recovery Options */}
                    {showAdvanced && (
                      <div className="pt-2 border-t border-red-500/20">
                        <div className="text-xs text-slate-400 mb-2">Having persistent issues?</div>
                        <button
                          onClick={handleClearSessionData}
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/30 text-orange-400 text-xs font-cyberpunk-nav rounded transition-colors duration-200 disabled:opacity-50"
                        >
                          CLEAR_SESSION_DATA
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !email || !password}
                  className="w-full bg-gradient-to-r from-cyan-600/80 to-purple-600/80 hover:from-cyan-500/90 hover:to-purple-500/90 disabled:from-slate-600/50 disabled:to-slate-600/50 text-white font-cyberpunk-nav py-3 px-6 rounded-lg transition-all duration-300 disabled:cursor-not-allowed relative overflow-hidden group"
                >
                  <span className="relative z-10 uppercase tracking-widest text-sm">
                    {isSubmitting ? 'Authenticating...' : 'Access Portal'}
                  </span>
                  {!isSubmitting && (
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
                  )}
                </button>
              </form>

              {/* Back Button */}
              <button
                onClick={onBack}
                disabled={isSubmitting}
                className="w-full mt-4 flex items-center justify-center space-x-2 text-slate-400 hover:text-cyan-400 transition-colors duration-300 py-2 font-cyberpunk-nav text-xs uppercase tracking-widest disabled:opacity-50"
              >
                <ArrowLeft className="h-3 w-3" />
                <span>Return to Main Site</span>
              </button>

              {/* Divider */}
              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600/50 to-transparent"></div>
                <div className="px-4 text-slate-500 text-xs font-cyberpunk-nav">SECURE</div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600/50 to-transparent"></div>
              </div>

              {/* Footer Attribution */}
              <div className="text-center space-y-2">
                <div className="text-xs text-slate-500 font-cyberpunk-nav">
                  INVITATION ONLY ACCESS
                </div>
                <div className="flex items-center justify-center space-x-2 text-xs text-slate-400">
                  <span>Powered by</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="font-cyberpunk-nav text-green-400">Supabase</span>
                  </div>
                </div>
                <div className="text-xs text-slate-600 font-cyberpunk-nav">
                  Enterprise Authentication • Real-time Security
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Accent */}
          <div className="text-center mt-6">
            <div className="inline-flex items-center space-x-2 text-xs text-slate-500 font-cyberpunk-nav">
              <div className="w-1 h-1 bg-cyan-400 rounded-full animate-pulse"></div>
              <span>COSMIC FORCES SECURE NETWORK</span>
              <div className="w-1 h-1 bg-cyan-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};